import type {ReactNode} from 'react';
import {useId} from 'react';
import {Theme} from '@wellstone-solutions/common';
import {Box, Stack, Typography} from '@wellstone-solutions/web';

const ROW_RULE = 'rgba(0, 0, 0, 0.08)';

export type SummaryListItem = {label: string; detail?: string};

export type SummaryValue = string | readonly SummaryListItem[];

export type SummaryField = {
  label: string;
  value: SummaryValue;
};

export type SummaryFact = {
  label: string;
  value: string;
};

const hasAnswer = ({value}: SummaryField) =>
  typeof value === 'string' ? value.trim() !== '' : value.length > 0;

const overlineSx = {
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '1px',
  lineHeight: '20px',
  textTransform: 'uppercase',
  color: Theme.colorPalette.primary,
} as const;

const List = ({items}: {items: readonly SummaryListItem[]}) => (
  <Stack component="ul" spacing={1.25} sx={{m: 0, p: 0, listStyle: 'none'}}>
    {items.map(({label, detail}) => (
      <Box component="li" key={label} sx={{display: 'flex', gap: 1.25}}>
        <Box
          aria-hidden
          sx={{
            flexShrink: 0,
            width: 6,
            height: 6,
            mt: '7px',
            borderRadius: '50%',
            bgcolor: Theme.colorPalette.primary,
          }}
        />
        <span>
          {label}
          {detail && (
            <Box component="span" sx={{color: 'text.secondary'}}>
              : {detail}
            </Box>
          )}
        </span>
      </Box>
    ))}
  </Stack>
);

const Root = ({children}: {children: ReactNode}) => (
  <Stack spacing={3}>{children}</Stack>
);

const Facts = ({facts}: {facts: readonly SummaryFact[]}) => (
  <Box
    component="dl"
    sx={{
      m: 0,
      display: 'grid',
      gridTemplateColumns: {xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)'},
      // The gap shows the border colour behind the cells as dividers
      gap: '1px',
      bgcolor: 'divider',
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      overflow: 'hidden',
    }}>
    {facts.map(({label, value}) => (
      <Box key={label} sx={{bgcolor: 'background.paper', px: 2, py: 1.5}}>
        <Box
          component="dt"
          sx={{fontSize: 12, lineHeight: '16px', color: 'text.secondary'}}>
          {label}
        </Box>
        <Box
          component="dd"
          sx={{
            m: 0,
            mt: 0.5,
            fontSize: 15,
            fontWeight: 500,
            lineHeight: '20px',
          }}>
          {value}
        </Box>
      </Box>
    ))}
  </Box>
);

type NarrativeProps = {
  text: string;
  action?: ReactNode;
};

const Narrative = ({text, action}: NarrativeProps) => {
  const titleId = useId();

  if (!text.trim()) {
    return null;
  }

  return (
    <Box component="section" aria-labelledby={titleId}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{mb: 1}}>
        <Typography id={titleId} component="h3" sx={overlineSx}>
          Narrative
        </Typography>
        {action}
      </Stack>
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          px: 2,
          py: 1.75,
          fontSize: 15,
          lineHeight: 1.6,
          whiteSpace: 'pre-line',
        }}>
        {text}
      </Box>
    </Box>
  );
};

type SectionProps = {
  title: string;
  fields: readonly SummaryField[];
};

const Section = ({title, fields}: SectionProps) => {
  const titleId = useId();
  const answered = fields.filter(hasAnswer);

  if (answered.length === 0) {
    return null;
  }

  return (
    <Box component="section" aria-labelledby={titleId}>
      <Typography
        id={titleId}
        component="h3"
        sx={{
          ...overlineSx,
          pb: 1,
          borderBottom: `2px solid ${Theme.colorPalette.primary4}`,
        }}>
        {title}
      </Typography>
      <Box component="dl" sx={{m: 0}}>
        {answered.map(({label, value}) => (
          <Box
            key={label}
            sx={{
              display: 'grid',
              gridTemplateColumns: {xs: '1fr', sm: '180px 1fr'},
              gap: {xs: 0.5, sm: 2},
              py: 1.5,
              borderBottom: `1px solid ${ROW_RULE}`,
            }}>
            <Box
              component="dt"
              sx={{fontSize: 13, lineHeight: '20px', color: 'text.secondary'}}>
              {label}
            </Box>
            <Box
              component="dd"
              sx={{
                m: 0,
                fontSize: 15,
                lineHeight: '20px',
                whiteSpace: 'pre-line',
              }}>
              {typeof value === 'string' ? value : <List items={value} />}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const NoteSummary = Object.assign(Root, {Facts, Narrative, Section});
